import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import Country from '../../../../../../types/country/Country';

import PhysicalSampleFirmwareUpgradeDTO from '../../../../../../types/physical-sample/PhysicalSampleFirmwareUpgradeDTO';
import SampleFirmwareRevision from '../../../../../../types/sample-firmware-revision/SampleFirmwareRevision';
import SampleFirmwareRevisionWriteDTO
    from '../../../../../../types/sample-firmware-revision/SampleFirmwareRevisionWriteDTO';
import Studio from '../../../../../../types/studio/Studio';
import { SampleFirmwareRevisionService } from '../../../../services/sample-firmware-revision.service';

@Component({
    selector: 'app-upgrade-physical-sample-firmware-dialog',
    imports: [
    DialogModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    IftaLabelModule,
    MultiSelectModule,
    Select
],
    templateUrl: './upgrade-physical-sample-firmware-dialog.component.html',
    styleUrl: './upgrade-physical-sample-firmware-dialog.component.scss'
})
export class UpgradePhysicalSampleFirmwareDialogComponent implements OnChanges {

  @Input() public visible: boolean = false;
  @Input() public allAvailableStudios: Studio[] = [];
  @Input() public allAvailableCountries: Country[] = [];
  @Input() public allAvailableTestedSampleFirmwareRevisions: SampleFirmwareRevision[] = [];
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public choosePhysicalSampleForFirmwareUpgrade: EventEmitter<PhysicalSampleFirmwareUpgradeDTO> = new EventEmitter<PhysicalSampleFirmwareUpgradeDTO>();

  public sampleFirmwareRevision: SampleFirmwareRevisionWriteDTO = new SampleFirmwareRevisionWriteDTO({});

  public loading = false;

    @Input() public firmwareUpgradeRequirements = [
        'Test Install',
        'Install',
        'Re-Install'
    ];
  public firmwareUpgradeReleaseScope = ['Global Release', 'Country Specific Release', 'Studio Specific Release'];
  public firmwareUpgradeReleaseWindow = ['Immediate Release', 'Maintenance Window Release'];
  public selectedFirmwareUpgradeRequirement : string | undefined;
  public selectedFirmwareUpgradeReleaseScope : string | undefined;
  public selectedFirmwareUpgradeReleaseWindow : string | undefined;
  public selectedCompatibleFirmwareRevision : SampleFirmwareRevision | undefined;
  public selectedStudios : Studio[] = [];
  public selectedCountries : Country[] = [];
  public allFirmwareRevisions : SampleFirmwareRevision[] = [];
  public compatibleFirmwareRevisions : SampleFirmwareRevision[] = [];
  public showFirmwareUpgradeReleaseScopeChooser: boolean = false;
  public showStudioChooser: boolean = false;
  public showCountryChooser: boolean = false;

  public preSelectAll: boolean = false;
  public immediate: boolean = false;

  constructor(private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
              private messageService: MessageService) {
  }

  /**
   * Loads the firmware revisions once the dialog is actually opened, not on component creation,
   * since this data is only available to admins and the dialog is always present in the DOM.
   */
  ngOnChanges(changes: SimpleChanges): void {
      if (changes['visible']?.currentValue === true && this.allFirmwareRevisions.length === 0) {
          this.loading = true;
          this.sampleFirmwareRevisionService.getAllSampleFirmwareRevisions()
              .then((sampleFirmwareRevisions: SampleFirmwareRevision[]) => {
                  this.allFirmwareRevisions = sampleFirmwareRevisions;
              })
              .catch((error: any) => {
                  this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
              })
              .finally(() => {
                  this.loading = false;
              });
      }
  }

  /**
   * Enables or disables the physicak sample chooser based on required fields selection.
   */
  get choosePhysicalSamplesDisabled(): boolean {
      if(this.selectedFirmwareUpgradeRequirement === undefined || this.selectedCompatibleFirmwareRevision === undefined || this.selectedFirmwareUpgradeReleaseWindow === undefined) {
        return true;
      }
      switch(this.selectedFirmwareUpgradeRequirement) {
        case 'Test Install':
          return !this.selectedCompatibleFirmwareRevision || !this.selectedStudios.length;

        case 'Install':
          switch(this.selectedFirmwareUpgradeReleaseScope) {
            case 'Global Release':
              return !this.selectedCompatibleFirmwareRevision;
            case 'Country Specific Release':
              return !this.selectedCompatibleFirmwareRevision || !this.selectedCountries.length;
            case 'Studio Specific Release':
              return !this.selectedCompatibleFirmwareRevision || !this.selectedStudios.length;
            default:
              return !this.selectedCompatibleFirmwareRevision;
          }
        
          case 'Re-Install':
          return !this.selectedCompatibleFirmwareRevision || !this.selectedStudios.length;
        default:
          return !this.selectedFirmwareUpgradeRequirement;
      }
  }

  /**
   * Closes the update dialog box and resets the status variables.
   */
  public close() {
      this.visible = false;
      this.showFirmwareUpgradeReleaseScopeChooser = false;
      this.showStudioChooser = false;
      this.showCountryChooser = false;
      this.sampleFirmwareRevision = new SampleFirmwareRevisionWriteDTO({});
      this.selectedFirmwareUpgradeRequirement = undefined;
      this.selectedCompatibleFirmwareRevision = undefined ;
      this.selectedFirmwareUpgradeReleaseWindow = undefined;
      this.selectedStudios = [];
      this.selectedCountries = [];
      this.preSelectAll = false;
      this.visibleChange.emit(this.visible);
  }

  /**
   * Upon selection of firmware upgrade requirement.
   */
  public onFirmwareUpgradeRequirementSelect(value: string) {
    switch (value) {
      case 'Test Install':
        this.resetUpgradeChooserSelection();
        this.showFirmwareUpgradeReleaseScopeChooser = false;
        this.showStudioChooser = true;
        this.compatibleFirmwareRevisions = this.allFirmwareRevisions;
        break;
      case 'Install':
        this.resetUpgradeChooserSelection();
        this.showFirmwareUpgradeReleaseScopeChooser = true;
        this.compatibleFirmwareRevisions = this.allFirmwareRevisions.filter(fr => fr.installationStatus === 'READY_TO_BE_INSTALLED')
        break;
      case 'Re-Install':
        this.resetUpgradeChooserSelection();
        this.showFirmwareUpgradeReleaseScopeChooser = true;
        this.showStudioChooser = true;
        this.compatibleFirmwareRevisions = this.allFirmwareRevisions.filter(fr => ['ACTIVE', 'ARCHIVED'].includes(fr.installationStatus))
        break;
    }
  }

  /**
   * Closes the firmware upgrade dialog box and resets the related states.
   */
  public resetUpgradeChooserSelection(): void {
    this.selectedCompatibleFirmwareRevision = undefined;
    this.selectedFirmwareUpgradeReleaseScope = undefined;
    this.selectedFirmwareUpgradeReleaseWindow = undefined;
    this.immediate = false;
    this.selectedStudios = [];
    this.selectedCountries = [];
  }

  /**
   * Upon selection of firmware upgrade release scope. Not confirmed here - only confirmed right before the upgrade is
   * actually triggered.
   */
  public onFirmwareUpgradeReleaseScopeSelect(value: string) {
    switch (value) {
      case 'Global Release':
          this.showStudioChooser = false;
          this.showCountryChooser = false;
          this.selectedStudios = [];
          this.selectedCountries = [];
          this.preSelectAll = true;
        break;
      case 'Country Specific Release':
          this.showStudioChooser = false;
          this.showCountryChooser = true;
          this.selectedStudios = [];
          this.preSelectAll = true;
        break;
      case 'Studio Specific Release':
        this.showCountryChooser = false;
        this.showStudioChooser = true;
          this.preSelectAll = true;
        break;
    }
  }

  /**
   * Upon selection of firmware upgrade execution window.
   */
  public onFirmwareUpgradeReleaseWindowSelect(value: string) {
    switch (value) {
      case 'Immediate Release':
        this.immediate = true;
        break;
      case 'Maintenance Window Release':
        this.immediate = false;
        break;
    }
  }

  /**
   * Propagates the selected options along with firmware upgrade request data to the sample chooser mode.
   */
  public choosePhysicalSamples() {
      if (this.selectedFirmwareUpgradeRequirement === undefined || this.selectedCompatibleFirmwareRevision ===
          undefined) {
          this.messageService.add(
              { severity: 'error', summary: 'Error', detail: 'Please choose required', life: 3000 });
          return;
      }
      const physicalSampleFirmwareUpgradeDTO = new PhysicalSampleFirmwareUpgradeDTO(
          this.selectedFirmwareUpgradeRequirement,
          this.selectedCompatibleFirmwareRevision,
          this.selectedCountries,
          this.selectedStudios,
          this.immediate,
          this.preSelectAll
      );
      this.choosePhysicalSampleForFirmwareUpgrade.emit(physicalSampleFirmwareUpgradeDTO);
      this.close();
  }
}
